import { useMemo } from "react"
import { Badge } from "@/components/ui/badge"
import { ScrollArea } from "@/components/ui/scroll-area"
import { TooltipProvider } from "@/components/ui/tooltip"
import { SidebarAssignmentAvatar, type BidMemberInfo } from "@/components/ui/owner-assignment"
import type { QuestionnaireItem } from "@/types/questionnaire-types"
import type { SectionAssignment } from "@/stores/section-assignments-store"
import { useSectionFlagsStore, mergeFlagsBySection, type MergedSectionFlag } from "@/stores/section-flags-store"
import { useAuthStore } from "@/stores/auth-store"
import { collaborationStatusConfig, type CollaborationStatusKey } from "@/lib/section-status"
import { SectionFlagMarker } from "@/components/drafting/SectionFlagMarker"
import { cn } from "@/lib/utils"

// ===================
// Types
// ===================

interface QuestionNavigatorProps {
  questions: QuestionnaireItem[]
  selectedSectionKey: string | null
  onQuestionSelect: (sectionKey: string) => void
  assignments: SectionAssignment[]
  bidMembers: BidMemberInfo[]
  isOwner: boolean
  isFinalized: boolean
  onAssignSection: (sectionKey: string, userId: string) => void
  onUnassignSection: (sectionKey: string) => void
  /** Artifact whose section flags to surface (optional — omit to hide markers). */
  artifactId?: string
}

interface QuestionGroup {
  title: string | null
  questions: QuestionnaireItem[]
}

// ===================
// Helpers
// ===================

function groupQuestions(questions: QuestionnaireItem[]): QuestionGroup[] {
  const groups: QuestionGroup[] = []
  let currentGroup: string | null | undefined

  for (const q of questions) {
    const group = q.groupTitle ?? null
    if (group !== currentGroup || groups.length === 0) {
      groups.push({ title: group, questions: [] })
      currentGroup = group
    }
    groups[groups.length - 1]!.questions.push(q)
  }
  return groups
}

// ===================
// Sub-components
// ===================

function QuestionNavItem({
  question,
  isSelected,
  onSelect,
  status,
  assignment,
  currentUserId,
  bidMembers,
  canAssign,
  isFinalized,
  onAssign,
  onUnassign,
  flag,
}: {
  question: QuestionnaireItem
  isSelected: boolean
  onSelect: () => void
  status: CollaborationStatusKey
  assignment: SectionAssignment | undefined
  currentUserId: string | undefined
  bidMembers: BidMemberInfo[]
  canAssign: boolean
  isFinalized: boolean
  onAssign: (userId: string) => void
  onUnassign: () => void
  flag: MergedSectionFlag | undefined
}) {
  const statusInfo = collaborationStatusConfig[status]

  return (
    <button
      onClick={onSelect}
      className={cn(
        "w-full text-left px-3 py-2 transition-colors",
        isSelected
          ? "bg-foreground/10 border-l-2 border-l-[hsl(var(--brand))]"
          : "hover:bg-foreground/5 border-l-2 border-l-transparent",
        question.parentSectionKey && "pl-7"
      )}
    >
      <div className="flex items-center gap-2">
        {/* Assignment Avatar */}
        <SidebarAssignmentAvatar
          assignment={assignment}
          currentUserId={currentUserId}
          bidMembers={bidMembers}
          canAssign={canAssign}
          isFinalized={isFinalized}
          onAssign={onAssign}
          onUnassign={onUnassign}
          itemLabel="question"
        />

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-[13px] truncate flex-1 min-w-0">
              <span className="text-muted-foreground font-mono tabular-nums">{question.number}</span> {question.prompt}
            </p>
            {flag && <SectionFlagMarker sourceLabel={flag.sourceLabel} />}
            <div
              className={cn("h-2 w-2 rounded-full shrink-0", statusInfo.dotColor)}
              title={statusInfo.label}
              aria-label={statusInfo.label}
            />
          </div>
        </div>
      </div>
    </button>
  )
}

// ===================
// Main Component
// ===================

export function QuestionNavigator({
  questions,
  selectedSectionKey,
  onQuestionSelect,
  assignments,
  bidMembers,
  isOwner,
  isFinalized,
  onAssignSection,
  onUnassignSection,
  artifactId,
}: QuestionNavigatorProps) {
  const groupedQuestions = useMemo(() => groupQuestions(questions), [questions])
  const currentUserId = useAuthStore((s) => s.user?.id)
  const artifactFlags = useSectionFlagsStore((s) => (artifactId ? s.flagsByArtifact[artifactId] : undefined))
  const flagsBySection = useMemo(() => mergeFlagsBySection(artifactFlags ?? []), [artifactFlags])

  const getStatus = (sectionKey: string): CollaborationStatusKey => {
    const assignment = assignments.find((a) => a.sectionId === sectionKey)
    return (assignment?.collaborationStatus as CollaborationStatusKey) ?? 'not_started'
  }

  const getAssignment = (sectionKey: string): SectionAssignment | undefined => {
    return assignments.find((a) => a.sectionId === sectionKey)
  }

  return (
    <TooltipProvider delayDuration={300}>
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-3 pt-2">
        <div className="flex items-center justify-between h-8">
          <h3 className="text-sm font-medium text-muted-foreground">
            Questions
          </h3>
          <Badge variant="secondary" className="text-xs">
            {questions.length}
          </Badge>
        </div>
      </div>

      {/* Question list */}
      <ScrollArea className="flex-1">
        <div className="py-1">
          {groupedQuestions.map((group, gi) => (
            <div key={group.title ?? `__ungrouped_${gi}`}>
              {group.title && (
                <div className="px-3 py-1.5 mt-2 first:mt-0">
                  <span className="text-sm font-medium text-muted-foreground">
                    {group.title}
                  </span>
                </div>
              )}
              {group.questions.map((q) => (
                <QuestionNavItem
                  key={q.sectionKey}
                  question={q}
                  isSelected={q.sectionKey === selectedSectionKey}
                  onSelect={() => onQuestionSelect(q.sectionKey)}
                  status={getStatus(q.sectionKey)}
                  assignment={getAssignment(q.sectionKey)}
                  currentUserId={currentUserId}
                  bidMembers={bidMembers}
                  canAssign={isOwner}
                  isFinalized={isFinalized}
                  onAssign={(userId) => onAssignSection(q.sectionKey, userId)}
                  onUnassign={() => onUnassignSection(q.sectionKey)}
                  flag={flagsBySection.get(q.sectionKey)}
                />
              ))}
            </div>
          ))}
        </div>
      </ScrollArea>
    </div>
    </TooltipProvider>
  )
}
